import {
  IconButtonProps,
  IconButton,
  CircularProgress,
  Theme,
  Button,
  Fade,
  ButtonProps,
  useTheme,
} from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import React from 'react';

type AsyncButtonOwnProps = {
  loading: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  progressStyle?: React.CSSProperties;
};
type AsyncIconButtonProps = IconButtonProps & AsyncButtonOwnProps;

const StyleSheet = {
  AsyncIconButtonProgress: {
    width: 20,
    height: 20,
  },
  AsyncIconButton: {
    width: 20,
    height: 20,
  },
};

const useAsyncButtonStyles = makeStyles((theme: Theme) => ({
  AsyncButton: {
    borderRadius: '3px',
    paddingLeft: '14px',
    paddingRight: '14px',
    marginRight: '10px',
    fontWeight: 700,
  },
  slideUp: {
    animation: '$slideUp  600ms',
    animationFillMode: 'forwards',
  },
  '@keyframes slideUp': {
    from: {
      opacity: '1',
    },
    to: {
      opacity: '0',
      transform: 'translate3d(0, -60%, 0)',
    },
  },
  fadeIn: {
    animation: '$fadeIn',
    animationFillMode: 'forwards',
  },
  '@keyframes fadeIn': {
    from: {
      opacity: '0',
    },
    to: {
      opacity: '1',
    },
  },
  hidden: {
    display: 'none',
  },
  spinningLoader: {
    width: '20px',
    height: '20px',
    border: `2px solid ${theme.palette.primary.main}`,
    'border-left-color': 'white',
    'border-top-color': 'white',
    'border-radius': '50%',
    background: 'transparent',
    'animation-name': '$rotate-s-loader',
    'animation-iteration-count': 'infinite',
    'animation-duration': '0.8s',
    'animation-timing-function': 'linear',
    position: 'absolute',
  },
  '@keyframes rotate-s-loader': {
    from: {
      transform: 'rotate(0)',
    },
    to: {
      transform: 'rotate(360deg)',
    },
  },
}));

export const AsyncIconButton = ({
  loading,
  style = {},
  progressStyle = {},
  ...buttonProps
}: AsyncIconButtonProps) =>
  loading ? (
    <CircularProgress
      size="small"
      style={{ ...StyleSheet.AsyncIconButtonProgress, ...progressStyle }}
    />
  ) : (
    <IconButton {...buttonProps} style={{ ...StyleSheet.AsyncIconButton, ...style }}>
      {buttonProps.children}
    </IconButton>
  );

export const AsyncButton = ({
  loading,
  onClick,
  disabled,
  children,
  ...props
}: AsyncButtonOwnProps & ButtonProps) => {
  const classes = useAsyncButtonStyles();
  const theme = useTheme();

  const buttonStyle = {
    backgroundColor: theme.palette.primary.main,
    color: theme.specialColors.WHITE_TEXT,
  } as React.CSSProperties;
  let loaderClass = classes.hidden;

  if (loading) {
    loaderClass = classes.spinningLoader;
  } else if (disabled) {
    // loading
    buttonStyle.backgroundColor = undefined;
    buttonStyle.color = undefined;
  }

  return (
    <Button
      {...props}
      disabled={disabled}
      onClick={onClick}
      className={classes.AsyncButton}
      style={buttonStyle}
      variant="contained"
      color="primary"
    >
      <div className={loading ? classes.slideUp : classes.fadeIn}>{children}</div>
      <Fade in={loading} timeout={1100}>
        <div className={loaderClass} />
      </Fade>
    </Button>
  );
};
